import {
  CruiseControlType,
  FacialType,
  GameSettingType,
  VehicleBoardingType,
  WalkStyle,
} from "@1games-ru/region-shared/dist/enums/account/settings";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response";
import { Select, SelectOption, SoundButton } from "@/shared/ui";
import getTranslationSettingString from "@/domains/playerMenu/helpers/getTranslationSettingString";
import useGameSettings from "@/domains/settings/hooks/useGameSettings";
import PlayerMenuContent from "@/domains/playerMenu/components/PlayerMenuContent";
import PlayerMenuContentTitle from "@/domains/playerMenu/components/PlayerMenuContentTitle";
import PlayerMenuControlLabel from "@/domains/playerMenu/components/PlayerMenuControlLabel";
import PlayerMenuTooltip from "@/domains/playerMenu/components/PlayerMenuTooltip";
import useGameplayValues from "@/domains/settings/gameplay/hooks/useGameplayValues";
import {
  appliedGameplaySettingsSelector,
  reset,
  setAppliedSettings,
  setMemoizedSettings,
} from "@/domains/settings/gameplay/slice";

export default function PlayerMenuSettingsGameplayContainer() {
  const { t } = useTranslation("playerMenu");
  const { updateGameSettings, resetGameSettings, getGameSettings } = useGameSettings();
  const [isSaveSettings, setIsSaveSettings] = useState(false);
  const [isResetSettings, setIsResetSettings] = useState(false);
  const dispatch = useAppDispatch();
  const appliedGameplaySettings = useAppSelector(appliedGameplaySettingsSelector);
  const { cruiseControlValues, facialValues, walkStyleValues, vehicleEnterValues } =
    useGameplayValues();

  useEffect(() => {
    dispatch(setMemoizedSettings(appliedGameplaySettings));

    return () => {
      dispatch(reset());
    };
  }, [dispatch]);

  const resetToDefaultHandler = useCallback(async () => {
    setIsResetSettings(true);
    const settings = Object.keys(appliedGameplaySettings);
    const response = await resetGameSettings(settings as unknown as GameSettingType[]);

    if (response instanceof RpcMpProviderSuccessResponse) {
      await getGameSettings();
      dispatch(setMemoizedSettings(appliedGameplaySettings));
    } else {
      toast.error(response.message);
    }
    setIsResetSettings(false);
  }, [dispatch, appliedGameplaySettings]);

  const saveHandler = useCallback(async () => {
    setIsSaveSettings(true);
    const response = await updateGameSettings(appliedGameplaySettings);

    if (response instanceof RpcMpProviderSuccessResponse) {
      dispatch(setMemoizedSettings(appliedGameplaySettings));
    } else {
      toast.error(response.message);
    }
    setIsSaveSettings(false);
  }, [appliedGameplaySettings, dispatch]);

  const header = (
    <PlayerMenuContentTitle>{t("playerMenu.tabTitle.Gameplay")}</PlayerMenuContentTitle>
  );

  const footer = (
    <div className="flex items-center justify-between">
      <SoundButton
        className="h-[2.75rem]"
        loading={isSaveSettings}
        disabled={isSaveSettings || isResetSettings}
        onClick={saveHandler}
      >
        Сохранить изменения
      </SoundButton>
      <SoundButton
        variant="soft"
        color="gray"
        className="h-[2.75rem]"
        loading={isResetSettings}
        disabled={isResetSettings || isSaveSettings}
        onClick={resetToDefaultHandler}
      >
        Сбрость к стандартным
      </SoundButton>
    </div>
  );

  if (!appliedGameplaySettings) {
    return null;
  }

  return (
    <PlayerMenuContent header={header} footer={footer}>
      <div className="grid grid-cols-2 gap-5">
        <div>
          <div className="flex items-center justify-between">
            <PlayerMenuControlLabel>
              {t(getTranslationSettingString(GameSettingType.SettingWalkStyleType))}
            </PlayerMenuControlLabel>
            <PlayerMenuTooltip text="?" />
          </div>
          <Select
            value={t(
              `playerMenu.walkStyleType.${WalkStyle[appliedGameplaySettings[GameSettingType.SettingWalkStyleType]]}`,
            )}
            bg="bg-dark-800"
            className="mt-3 z-3"
          >
            {walkStyleValues.map((item) => (
              <SelectOption
                key={item}
                onClick={() =>
                  dispatch(setAppliedSettings({ [GameSettingType.SettingWalkStyleType]: item }))
                }
                isSelected={item === appliedGameplaySettings[GameSettingType.SettingWalkStyleType]}
                className="hover:text-accent-solid transition-all"
              >
                {t(`playerMenu.walkStyleType.${WalkStyle[item]}`)}
              </SelectOption>
            ))}
          </Select>
        </div>
        <div>
          <div className="flex items-center justify-between">
            <PlayerMenuControlLabel>
              {t(getTranslationSettingString(GameSettingType.SettingCruiseControlType))}
            </PlayerMenuControlLabel>
            <PlayerMenuTooltip text="?" />
          </div>
          <Select
            value={t(
              `playerMenu.cruiseControlType.${CruiseControlType[appliedGameplaySettings[GameSettingType.SettingCruiseControlType]]}`,
            )}
            bg="bg-dark-800"
            className="mt-3 z-2"
          >
            {cruiseControlValues.map((item) => (
              <SelectOption
                key={item}
                onClick={() =>
                  dispatch(
                    setAppliedSettings({
                      [GameSettingType.SettingCruiseControlType]: item,
                    }),
                  )
                }
                isSelected={
                  item === appliedGameplaySettings[GameSettingType.SettingCruiseControlType]
                }
                className="hover:text-accent-solid transition-all"
              >
                {t(`playerMenu.cruiseControlType.${CruiseControlType[item]}`)}
              </SelectOption>
            ))}
          </Select>
        </div>
        <div>
          <div className="flex items-center justify-between">
            <PlayerMenuControlLabel>
              {t(getTranslationSettingString(GameSettingType.SettingFacialEmotionsType))}
            </PlayerMenuControlLabel>
            <PlayerMenuTooltip text="?" />
          </div>
          <Select
            value={t(
              `playerMenu.facialType.${FacialType[appliedGameplaySettings[GameSettingType.SettingFacialEmotionsType]]}`,
            )}
            bg="bg-dark-800"
            className="mt-3 z-1"
          >
            {facialValues.map((item) => (
              <SelectOption
                key={item}
                onClick={() =>
                  dispatch(
                    setAppliedSettings({
                      [GameSettingType.SettingFacialEmotionsType]: item,
                    }),
                  )
                }
                isSelected={
                  item === appliedGameplaySettings[GameSettingType.SettingFacialEmotionsType]
                }
                className="hover:text-accent-solid transition-all"
              >
                {t(`playerMenu.facialType.${FacialType[item]}`)}
              </SelectOption>
            ))}
          </Select>
        </div>
        <div>
          <div className="flex items-center justify-between">
            <PlayerMenuControlLabel>
              {t(getTranslationSettingString(GameSettingType.SettingVehicleEnterType))}
            </PlayerMenuControlLabel>
            <PlayerMenuTooltip text="?" />
          </div>
          <Select
            value={t(
              `playerMenu.vehicleEnterType.${VehicleBoardingType[appliedGameplaySettings[GameSettingType.SettingVehicleEnterType]]}`,
            )}
            bg="bg-dark-800"
            className="mt-3 z-1"
          >
            {vehicleEnterValues.map((item) => (
              <SelectOption
                key={item}
                onClick={() =>
                  dispatch(
                    setAppliedSettings({
                      [GameSettingType.SettingVehicleEnterType]: item,
                    }),
                  )
                }
                isSelected={
                  item === appliedGameplaySettings[GameSettingType.SettingVehicleEnterType]
                }
                className="hover:text-accent-solid transition-all"
              >
                {t(`playerMenu.vehicleEnterType.${VehicleBoardingType[item]}`)}
              </SelectOption>
            ))}
          </Select>
        </div>
      </div>
    </PlayerMenuContent>
  );
}
